/**
 * yazi-gravur-kopru.ts — Yazı → MEVCUT CAM gravür köprüsü (yeni paralel sistem DEĞİL).
 * Yazı outline'ını (font.generateShapes) FikoAktiviteGravur'un beklediği profil listesine
 * çevirir; mevcut çapraz-tarama (CrossHatch) toolpath + G-code + 3D toolpath görselini kullanır.
 */
import * as THREE from 'three';
import { FikoVektor3D } from '../FikoVektor3d';
import { FikoAktiviteGravur } from '../Fiko-2d-Cam/aktiviteler/gravur/FikoAktiviteGravur';
import { fontAl } from './fiko-yazi3d';

export interface IYaziGravurOpsiyon {
  fontId?: string; boyut?: number; harfBosluk?: number; egriSegment?: number;
  // Strateji
  gravurTipi?: 'CizgiGravur' | 'VGravur' | 'DolguGravur' | 'NoktaGravur';
  dolguDeseni?: 'Offset' | 'Raster' | 'CrossHatch' | 'Spiral';
  // Takım tanımı (FikoTakimFrezeleme)
  takimTipi?: string; takimAd?: string; takimCap?: number; takimRadius?: number; takimBoy?: number; takimKesmeBoy?: number;
  // Derinlik & Z
  derinlik?: number; ustZ?: number; guvenliZ?: number;
  // Dolgu
  dolguAdim?: number; aci?: number; crossAci?: number; overlap?: number; dolguCevre?: boolean; dolguCevreSayisi?: number;
  // V-Gravür
  vAci?: number; vMaxDerinlik?: number; vMinDerinlik?: number; vOtomatikDerinlik?: boolean; vFlatDerinlik?: number; vFlatTakim?: boolean; vFlatTakimCap?: number;
  // Çizgi gravür
  cizgiDerinlik?: number; cizgiGenislik?: number; cizgiOffset?: 'Merkez' | 'Sol' | 'Sag';
  // Nokta gravür
  noktaAralik?: number; noktaDerinlik?: number; noktaDesen?: 'Grid' | 'Hex' | 'Random';
  // Hızlar
  devir?: number; ilerlemeGravur?: number; ilerlemeDalma?: number; ilerlemeGecis?: number; hizRapid?: number;
  // Giriş/Çıkış
  girisYontemi?: 'Dikey' | 'Rampa' | 'Yay'; girisUzunluk?: number; girisRampaAcisi?: number; cikisYontemi?: 'Dikey' | 'Rampa' | 'Yay'; cikisUzunluk?: number;
  // Optimizasyon
  siralama?: 'Secimsirasi' | 'EnKisaYol' | 'SoldanSaga' | 'YukaridanAsagi'; grupla?: boolean; grupMesafe?: number;
  // Çoklu geçiş
  cokluGecisEtkin?: boolean; gecisDerinlik?: number; gecisPayi?: number;
  // Stay-down
  stayDownEtkin?: boolean; stayDownMesafe?: number; stayDownZ?: number;
}

/** Takım tipine göre efektif tarama genişliği (mm) — adım bundan türetilir. */
export function takimEfektifGenislik(tip: string, cap: number, derinlik: number, vAci = 30): number {
  if (tip === 'VKesici' || tip === 'Elmas') {
    const g = 2 * Math.max(0.01, derinlik) * Math.tan((vAci / 2) * Math.PI / 180);
    return Math.min(cap, Math.max(0.05, g));   // derinlikten gelen V izi (çapla sınırlı)
  }
  return Math.max(0.05, cap);                  // ParmakFreze (düz) / BallNose (küre) → çap
}

/** Yazıyı kapalı kontur (dış) + delik (iç boşluk) listelerine açar — XY düzleminde. */
export function yaziOutlineCikar(metin: string, fontId: string, boyut = 10, harfBosluk = 0, egriSegment = 8):
  { konturlar: FikoVektor3D[][]; delikler: FikoVektor3D[][] } {
  const font = fontAl(fontId);
  const konturlar: FikoVektor3D[][] = [], delikler: FikoVektor3D[][] = [];
  if (!font || !metin) return { konturlar, delikler };
  let x = 0;
  for (const ch of metin) {
    if (ch === ' ') { x += boyut * 0.45 + harfBosluk; continue; }
    const shapes: THREE.Shape[] = (font as any).generateShapes(ch, boyut);
    if (!shapes?.length) { x += boyut * 0.45 + harfBosluk; continue; }
    let harfMaxX = 0;
    for (const shape of shapes) {
      const dis = shape.getPoints(egriSegment);
      if (dis.length >= 3) { konturlar.push(dis.map(p => new FikoVektor3D(p.x + x, p.y, 0))); for (const p of dis) if (p.x > harfMaxX) harfMaxX = p.x; }
      for (const hole of (shape.holes || [])) {
        const hp = hole.getPoints(egriSegment);
        if (hp.length >= 3) delikler.push(hp.map(p => new FikoVektor3D(p.x + x, p.y, 0)));
      }
    }
    x += harfMaxX + harfBosluk + boyut * 0.1;
  }
  return { konturlar, delikler };
}

/** Yazıyı MEVCUT gravür aktivitesine yükler + tüm CATIA-CAM parametrelerini uygular + toolpath hesaplar. */
export function yaziGravurUygula(aktivite: FikoAktiviteGravur, metin: string, opsiyon: IYaziGravurOpsiyon = {}): void {
  const a = aktivite as any;
  const { konturlar } = yaziOutlineCikar(metin, opsiyon.fontId || 'helvetiker', opsiyon.boyut ?? 10, opsiyon.harfBosluk ?? 2, opsiyon.egriSegment ?? 8);
  a.temizle?.();
  aktivite.profillerEkle(konturlar);
  aktivite.gravurTipi = opsiyon.gravurTipi ?? 'DolguGravur';
  aktivite.dolguDeseni = opsiyon.dolguDeseni ?? 'CrossHatch';

  // Takım tanımı (MEVCUT FikoTakimFrezeleme — tip/çap/köşe/boy)
  if (aktivite.takim) {
    const t = aktivite.takim;
    if (opsiyon.takimTipi) t.takimTipi = opsiyon.takimTipi;
    if (opsiyon.takimAd) t.ad = opsiyon.takimAd;
    t.cap = opsiyon.takimCap ?? t.cap ?? 1;
    if (opsiyon.takimRadius != null) t.radius = opsiyon.takimRadius;
    if (opsiyon.takimBoy != null) t.boy = opsiyon.takimBoy;
    if (opsiyon.takimKesmeBoy != null) t.kesmeBoy = opsiyon.takimKesmeBoy;
    (t as any).hazirla?.();
  }

  // Derinlik & Z
  const derinlik = opsiyon.derinlik ?? 0.4;
  a.gravurDerinlik = derinlik;
  if (opsiyon.ustZ != null) a.ustZ = opsiyon.ustZ;
  if (opsiyon.guvenliZ != null) a.guvenliZ = opsiyon.guvenliZ;

  // Dolgu — TARAMA TANITILAN TAKIMA GÖRE: adım boşsa takım efektif genişliği × (1−bindirme)
  const bindirme = Math.min(0.9, Math.max(0, opsiyon.overlap ?? 0));
  const efGen = takimEfektifGenislik(opsiyon.takimTipi || 'VKesici', aktivite.takim?.cap ?? 1, derinlik, opsiyon.vAci ?? 30);
  a.dolguAdim = opsiyon.dolguAdim ?? Math.max(0.05, efGen * (1 - bindirme));
  a.dolguAci = opsiyon.aci ?? 0;
  a.dolguCrossAci = opsiyon.crossAci ?? 90;
  a.dolguOverlap = 0;   // bindirme adıma dahil edildi (çift sayma yok)
  a.dolguCevre = opsiyon.dolguCevre ?? true;
  if (opsiyon.dolguCevreSayisi != null) a.dolguCevreSayisi = opsiyon.dolguCevreSayisi;

  // V-Gravür
  if (opsiyon.vAci != null) a.vAci = opsiyon.vAci;
  if (opsiyon.vMaxDerinlik != null) a.vMaxDerinlik = opsiyon.vMaxDerinlik;
  if (opsiyon.vMinDerinlik != null) a.vMinDerinlik = opsiyon.vMinDerinlik;
  if (opsiyon.vOtomatikDerinlik != null) a.vOtomatikDerinlik = opsiyon.vOtomatikDerinlik;
  if (opsiyon.vFlatDerinlik != null) a.vFlatDerinlik = opsiyon.vFlatDerinlik;
  if (opsiyon.vFlatTakim != null) a.vFlatTakim = opsiyon.vFlatTakim;
  if (opsiyon.vFlatTakimCap != null) a.vFlatTakimCap = opsiyon.vFlatTakimCap;

  // Çizgi gravür
  if (opsiyon.cizgiDerinlik != null) a.cizgiDerinlik = opsiyon.cizgiDerinlik;
  if (opsiyon.cizgiGenislik != null) a.cizgiGenislik = opsiyon.cizgiGenislik;
  if (opsiyon.cizgiOffset != null) a.cizgiOffset = opsiyon.cizgiOffset;

  // Nokta gravür
  if (opsiyon.noktaAralik != null) a.noktaAralik = opsiyon.noktaAralik;
  if (opsiyon.noktaDerinlik != null) a.noktaDerinlik = opsiyon.noktaDerinlik;
  if (opsiyon.noktaDesen != null) a.noktaDesen = opsiyon.noktaDesen;

  // Hızlar
  if (opsiyon.devir != null) a.devir = opsiyon.devir;
  if (opsiyon.ilerlemeGravur != null) a.ilerlemeGravur = opsiyon.ilerlemeGravur;
  if (opsiyon.ilerlemeDalma != null) a.ilerlemeDalma = opsiyon.ilerlemeDalma;
  if (opsiyon.ilerlemeGecis != null) a.ilerlemeGecis = opsiyon.ilerlemeGecis;
  if (opsiyon.hizRapid != null) a.hizRapid = opsiyon.hizRapid;

  // Giriş/Çıkış
  if (opsiyon.girisYontemi != null) a.girisYontemi = opsiyon.girisYontemi;
  if (opsiyon.girisUzunluk != null) a.girisUzunluk = opsiyon.girisUzunluk;
  if (opsiyon.girisRampaAcisi != null) a.girisRampaAcisi = opsiyon.girisRampaAcisi;
  if (opsiyon.cikisYontemi != null) a.cikisYontemi = opsiyon.cikisYontemi;
  if (opsiyon.cikisUzunluk != null) a.cikisUzunluk = opsiyon.cikisUzunluk;

  // Optimizasyon
  if (opsiyon.siralama != null) a.siralama = opsiyon.siralama;
  if (opsiyon.grupla != null) a.grupla = opsiyon.grupla;
  if (opsiyon.grupMesafe != null) a.grupMesafe = opsiyon.grupMesafe;

  // Çoklu geçiş + Stay-down
  if (opsiyon.cokluGecisEtkin != null) a.cokluGecisEtkin = opsiyon.cokluGecisEtkin;
  if (opsiyon.gecisDerinlik != null) a.gecisDerinlik = opsiyon.gecisDerinlik;
  if (opsiyon.gecisPayi != null) a.gecisPayi = opsiyon.gecisPayi;
  if (opsiyon.stayDownEtkin != null) a.stayDownEtkin = opsiyon.stayDownEtkin;
  if (opsiyon.stayDownMesafe != null) a.stayDownMesafe = opsiyon.stayDownMesafe;
  if (opsiyon.stayDownZ != null) a.stayDownZ = opsiyon.stayDownZ;

  aktivite.takimYoluHesapla();
}

/** Geriye dönük uyumluluk: eski isim → genel uygulayıcı. */
export const yaziCaprazTaramaUygula = yaziGravurUygula;
